import { useState, useEffect, useCallback } from 'react';
import {
  KnowledgeBaseStats,
  Document,
  KnowledgeBase,
  KnowledgeBaseApiResponse,
  DocumentApiResponse,
  KnowledgeBaseFilters,
  UploadDocumentData,
  CreateKnowledgeBaseData,
} from '../types';
import { ApiClient } from '@utils/apiClient';
import { handleApiResult, handleApiCatch } from '@utils/apiErrorHandler';

export const useKnowledgeBase = () => {
  const [stats, setStats] = useState<KnowledgeBaseStats | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<KnowledgeBaseFilters>({
    search: '',
    status: '',
    knowledgeBase: '',
  });

  const fetchStats = useCallback(async () => {
    try {
      const response = await ApiClient.get('/knowledge-base/stats/');
      const result = await handleApiResult<KnowledgeBaseStats>(
        response,
        'Failed to fetch knowledge base stats'
      );

      if (result.success && result.data) {
        setStats(result.data);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch knowledge base stats');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch knowledge base stats');
      setError(processedErrors.generalError || 'Failed to fetch knowledge base stats');
    }
  }, []);

  const fetchKnowledgeBases = useCallback(async () => {
    try {
      const response = await ApiClient.get('/knowledge-base/');
      const result = await handleApiResult<KnowledgeBaseApiResponse[]>(
        response,
        'Failed to fetch knowledge bases'
      );

      if (result.success && result.data) {
        const transformedKnowledgeBases: KnowledgeBase[] = result.data.map(kb => ({
          id: kb.id,
          name: kb.name,
          description: kb.description,
          tags: kb.tags,
          status: kb.status,
          organization: kb.organization,
          documentCount: kb.document_count,
          created: kb.created,
          modified: kb.modified,
        }));
        setKnowledgeBases(transformedKnowledgeBases);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch knowledge bases');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch knowledge bases');
      setError(processedErrors.generalError || 'Failed to fetch knowledge bases');
    }
  }, []);

  const fetchDocuments = useCallback(async () => {
    try {
      // Initial load without filters
      const response = await ApiClient.get('/knowledge-base/documents/');
      const result = await handleApiResult<DocumentApiResponse[]>(
        response,
        'Failed to fetch documents'
      );

      if (result.success && result.data) {
        const transformedDocuments: Document[] = result.data.map(doc => ({
          id: doc.id,
          title: doc.title,
          knowledgeBase: {
            id: doc.knowledge_base.id,
            name: doc.knowledge_base.name,
            description: doc.knowledge_base.description,
            tags: doc.knowledge_base.tags,
            status: doc.knowledge_base.status,
            organization: doc.knowledge_base.organization,
            documentCount: doc.knowledge_base.document_count,
            created: doc.knowledge_base.created,
            modified: doc.knowledge_base.modified,
          },
          file: doc.file,
          fileSize: doc.file_size,
          fileSizeVerbose: doc.file_size_verbose,
          tags: doc.tags,
          status: doc.status,
          content: doc.content,
          metadata: doc.metadata,
          organization: doc.organization,
          created: doc.created,
          modified: doc.modified,
        }));
        setDocuments(transformedDocuments);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch documents');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch documents');
      setError(processedErrors.generalError || 'Failed to fetch documents');
    }
  }, []);

  const fetchDocumentsWithFilters = useCallback(async () => {
    try {
      setFilterLoading(true);
      const params = new URLSearchParams();
      if (filters.knowledgeBase) params.append('knowledge_base', filters.knowledgeBase);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);

      const response = await ApiClient.get(`/knowledge-base/documents/?${params.toString()}`);
      const result = await handleApiResult<DocumentApiResponse[]>(
        response,
        'Failed to fetch documents'
      );

      if (result.success && result.data) {
        const transformedDocuments: Document[] = result.data.map(doc => ({
          id: doc.id,
          title: doc.title,
          knowledgeBase: {
            id: doc.knowledge_base.id,
            name: doc.knowledge_base.name,
            description: doc.knowledge_base.description,
            tags: doc.knowledge_base.tags,
            status: doc.knowledge_base.status,
            organization: doc.knowledge_base.organization,
            documentCount: doc.knowledge_base.document_count,
            created: doc.knowledge_base.created,
            modified: doc.knowledge_base.modified,
          },
          file: doc.file,
          fileSize: doc.file_size,
          fileSizeVerbose: doc.file_size_verbose,
          tags: doc.tags,
          status: doc.status,
          content: doc.content,
          metadata: doc.metadata,
          organization: doc.organization,
          created: doc.created,
          modified: doc.modified,
        }));
        setDocuments(transformedDocuments);
      } else {
        setError(result.errors?.generalError || 'Failed to fetch documents');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to fetch documents');
      setError(processedErrors.generalError || 'Failed to fetch documents');
    } finally {
      setFilterLoading(false);
    }
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setInitialLoading(true);
    setError(null);
    try {
      await Promise.all([fetchStats(), fetchKnowledgeBases(), fetchDocuments()]);
    } catch (err) {
      setError('Failed to load knowledge base data');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [fetchStats, fetchKnowledgeBases, fetchDocuments]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const uploadDocument = async (documentData: UploadDocumentData) => {
    try {
      setLoading(true);

      // Step 1: Create document object
      const createResponse = await ApiClient.post('/knowledge-base/documents/', {
        title: documentData.title,
        knowledge_base: documentData.knowledgeBase,
        tags: documentData.tags,
        metadata: '',
      });

      const createResult = await handleApiResult<DocumentApiResponse>(
        createResponse,
        'Failed to create document'
      );

      if (createResult.success && createResult.data) {
        // Step 2: Upload file
        const formData = new FormData();
        formData.append('file', documentData.file);

        const uploadResponse = await ApiClient.postFormData(
          `/knowledge-base/documents/${createResult.data.id}/update-file/`,
          formData
        );

        if (uploadResponse.status === 200) {
          // Refresh documents list
          await fetchDocuments();
        } else {
          setError('Failed to upload file');
        }
      } else {
        setError(createResult.errors?.generalError || 'Failed to create document');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to upload document');
      setError(processedErrors.generalError || 'Failed to upload document');
    } finally {
      setLoading(false);
    }
  };

  const createKnowledgeBase = async (kbData: CreateKnowledgeBaseData) => {
    try {
      setLoading(true);

      const response = await ApiClient.post('/knowledge-base/', {
        name: kbData.name,
        description: kbData.description,
        tags: '',
        status: 'active',
      });

      const result = await handleApiResult<KnowledgeBaseApiResponse>(
        response,
        'Failed to create knowledge base'
      );

      if (result.success && result.data) {
        const newKnowledgeBase: KnowledgeBase = {
          id: result.data.id,
          name: result.data.name,
          description: result.data.description,
          tags: result.data.tags,
          status: result.data.status,
          organization: result.data.organization,
          documentCount: result.data.document_count,
          created: result.data.created,
          modified: result.data.modified,
        };
        setKnowledgeBases(prev => [...prev, newKnowledgeBase]);
      } else {
        setError(result.errors?.generalError || 'Failed to create knowledge base');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to create knowledge base');
      setError(processedErrors.generalError || 'Failed to create knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = async (id: string) => {
    try {
      setLoading(true);

      const response = await ApiClient.delete(`/knowledge-base/documents/${id}/`);

      if (response.status === 204) {
        setDocuments(prev => prev.filter(doc => doc.id !== id));
      } else {
        setError('Failed to delete document');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to delete document');
      setError(processedErrors.generalError || 'Failed to delete document');
    } finally {
      setLoading(false);
    }
  };

  const deleteKnowledgeBase = async (id: string) => {
    try {
      setLoading(true);

      const response = await ApiClient.delete(`/knowledge-base/${id}/`);

      if (response.status === 204) {
        setKnowledgeBases(prev => prev.filter(kb => kb.id !== id));
      } else {
        setError('Failed to delete knowledge base');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to delete knowledge base');
      setError(processedErrors.generalError || 'Failed to delete knowledge base');
    } finally {
      setLoading(false);
    }
  };

  const updateDocument = async (id: string, data: { title: string; tags: string }) => {
    try {
      setLoading(true);

      const response = await ApiClient.patch(`/knowledge-base/documents/${id}/`, {
        title: data.title,
        tags: data.tags,
      });

      const result = await handleApiResult<DocumentApiResponse>(
        response,
        'Failed to update document'
      );

      if (result.success && result.data) {
        const updatedDocument: Document = {
          id: result.data.id,
          title: result.data.title,
          knowledgeBase: {
            id: result.data.knowledge_base.id,
            name: result.data.knowledge_base.name,
            description: result.data.knowledge_base.description,
            tags: result.data.knowledge_base.tags,
            status: result.data.knowledge_base.status,
            organization: result.data.knowledge_base.organization,
            documentCount: result.data.knowledge_base.document_count,
            created: result.data.knowledge_base.created,
            modified: result.data.knowledge_base.modified,
          },
          file: result.data.file,
          fileSize: result.data.file_size,
          fileSizeVerbose: result.data.file_size_verbose,
          tags: result.data.tags,
          status: result.data.status,
          content: result.data.content,
          metadata: result.data.metadata,
          organization: result.data.organization,
          created: result.data.created,
          modified: result.data.modified,
        };

        setDocuments(prev => prev.map(doc => (doc.id === id ? updatedDocument : doc)));
      } else {
        setError(result.errors?.generalError || 'Failed to update document');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to update document');
      setError(processedErrors.generalError || 'Failed to update document');
    } finally {
      setLoading(false);
    }
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(filters.search.toLowerCase());
    const matchesStatus = filters.status === '' || doc.status === filters.status;
    const matchesKnowledgeBase =
      filters.knowledgeBase === '' || doc.knowledgeBase.id === filters.knowledgeBase;
    return matchesSearch && matchesStatus && matchesKnowledgeBase;
  });

  const updateKnowledgeBase = async (
    id: string,
    data: { name: string; description: string; tags: string }
  ) => {
    try {
      setLoading(true);

      const response = await ApiClient.patch(`/knowledge-base/${id}/`, {
        name: data.name,
        description: data.description,
        tags: data.tags,
      });

      const result = await handleApiResult<KnowledgeBaseApiResponse>(
        response,
        'Failed to update knowledge base'
      );

      if (result.success && result.data) {
        const updatedKnowledgeBase: KnowledgeBase = {
          id: result.data.id,
          name: result.data.name,
          description: result.data.description,
          tags: result.data.tags,
          status: result.data.status,
          organization: result.data.organization,
          documentCount: result.data.document_count,
          created: result.data.created,
          modified: result.data.modified,
        };

        setKnowledgeBases(prev => prev.map(kb => (kb.id === id ? updatedKnowledgeBase : kb)));
      } else {
        setError(result.errors?.generalError || 'Failed to update knowledge base');
      }
    } catch (err) {
      const processedErrors = handleApiCatch(err, 'Failed to update knowledge base');
      setError(processedErrors.generalError || 'Failed to update knowledge base');
    } finally {
      setLoading(false);
    }
  };

  return {
    stats,
    documents: filteredDocuments,
    knowledgeBases,
    loading,
    initialLoading,
    filterLoading,
    error,
    filters,
    setFilters,
    uploadDocument,
    createKnowledgeBase,
    deleteDocument,
    deleteKnowledgeBase,
    updateDocument,
    updateKnowledgeBase,
    fetchDocumentsWithFilters,
    refetch: fetchData,
  };
};
